import React, { ReactNode, useEffect, useRef, useState } from "react";
import {
  Button,
  Stack,
  Popper,
  // ClickAwayListener,
  Typography,
  IconButton,
  Checkbox,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableCellProps
} from "@mui/material";
import { AsyncContentMui, Search } from "components";
import { useUserManageActions } from "store/actions-context";
import { useActionHook } from "hooks/shared-hooks/use-action-hook";
import { IUserManageInvestmentEntity } from "store/types";

const cellCommonStyles: TableCellProps["sx"] = {
  backgroundColor: "#edf1f9",
  color: "#0a3142",
  fontSize: "15px",
  fontWeight: "normal",
  border: "none",
  whiteSpace: "nowrap",
  maxHeight: "32px"
};

const rowCellStyles: TableCellProps["sx"] = {
  borderBottom: "0.5px solid #dae0e2",
  fontSize: "15px",
  color: "#26A6AE",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  overflow: "hidden",
  maxWidth: "250px",
  backgroundColor: "transparent",
  height: "32px"
};

interface Props {
  icon: ReactNode;
  userEntities: IUserManageInvestmentEntity[];
  onCancel: () => void;
  setUserEntities: (entities: IUserManageInvestmentEntity[]) => void;
  // onUpdateUserEntities: (userEntities: IUserManageInvestmentEntity[]) => void;
}

export const InvestmentEntity: React.FC<Props> = ({ icon, userEntities, onCancel, setUserEntities }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<IUserManageInvestmentEntity[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef<HTMLButtonElement>(null);

  const invalidateStates = () => {
    onCancel();
    setSearchTerm("");
    setSelectedEntity([]);
    setIsOpen(false);
  };

  const { getInvestmentEntitiesAction } = useUserManageActions();
  const { action: getInvestmentEntities, loading, data: entities } = useActionHook(getInvestmentEntitiesAction);

  useEffect(() => {
    getInvestmentEntities();
  }, []);

  useEffect(() => {
    if (entities && userEntities) {
      const preselectedIds = userEntities
        .filter((preselected) => entities.some((entity) => entity.id === preselected.id))
        .map((preselected) => preselected);

      setSelectedEntity(preselectedIds);
    }
  }, [userEntities, entities]);

  if (!entities) return null;

  const handleToggle = (entity: IUserManageInvestmentEntity) => {
    setSelectedEntity((prev) => (prev.includes(entity) ? prev.filter((e) => e !== entity) : [...prev, entity]));
  };

  const filteredEntities = entities.filter((entity) => entity.name.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <Stack gap="20px">
      <AsyncContentMui isLoading={loading} spinnerProps={{ size: 150 }}>
        <Stack direction="row" justifyContent="flex-end">
          <IconButton
            ref={containerRef}
            onClick={() => setIsOpen(true)}
            sx={{ backgroundColor: "#F6F8FC", height: "24px", width: "24px" }}
          >
            {icon}
          </IconButton>
        </Stack>
        {/* <ClickAwayListener onClickAway={() => setIsOpen(false)}>  */}
        <Popper
          placement="right-start"
          open={isOpen}
          anchorEl={containerRef.current}
          modifiers={[
            { name: "offset", options: { offset: [0, 10] } },
            { name: "preventOverflow", options: { boundary: "viewport", padding: 8 } },
            { name: "flip", options: { fallbackPlacements: ["left-start", "bottom-start", "top-start"] } }
          ]}
          sx={{
            width: "520px",
            zIndex: 1200,
            background: "#fff",
            padding: "20px",
            borderRadius: "8px",
            overflow: "hidden",
            boxShadow:
              "0px 2px 4px -1px rgba(0, 0, 0, 0.20), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)"
          }}
        >
          <Stack gap="20px">
            <Typography>Edit investment entity</Typography>
            <Stack flexDirection="row" justifyContent="flex-end">
              <div>
                <Search defaultValue={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
              </div>
            </Stack>
            <TableContainer
              sx={{
                borderRadius: "8px",
                boxShadow: "none",
                border: "0.5px solid #dae0e2",
                overflowX: "auto",
                maxHeight: "209px",
                height: "209px"
              }}
            >
              <Table
                sx={{
                  ".MuiTableCell-root": {
                    padding: "5px 26px"
                  }
                }}
                stickyHeader
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={cellCommonStyles} padding="checkbox">
                      <Checkbox
                        sx={{ padding: 0 }}
                        indeterminate={selectedEntity.length > 0 && selectedEntity.length < filteredEntities.length}
                        checked={selectedEntity.length > 0 && selectedEntity.length === filteredEntities.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedEntity(filteredEntities.map((entity) => entity));
                          } else {
                            setSelectedEntity([]);
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell sx={cellCommonStyles}>Investment Entity</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredEntities.map((entity) => (
                    <TableRow key={entity.id}>
                      <TableCell sx={rowCellStyles} padding="checkbox">
                        <Checkbox
                          sx={{ padding: 0 }}
                          checked={selectedEntity.some((selected) => selected.id === entity.id)}
                          onChange={() => handleToggle(entity)}
                        />
                      </TableCell>
                      <TableCell sx={rowCellStyles}>{entity.name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography sx={{ color: "#496682" }}>{selectedEntity.length} entities selected</Typography>
            <Stack direction="row" alignItems="center" justifyContent="flex-end" gap="6px">
              <Button variant="outlined" onClick={invalidateStates}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setUserEntities(selectedEntity);
                  setIsOpen(false);
                  setSearchTerm("");
                }}
                type="submit"
                variant="contained"
              >
                Confirm
              </Button>
            </Stack>
          </Stack>
        </Popper>
      </AsyncContentMui>
      {/* </ClickAwayListener> */}
    </Stack>
  );
};
